import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel,
  Button,
  Divider,
  CircularProgress,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Alert,
  Snackbar
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import FileUploadComponent from '../components/documents/FileUploadComponent';
import PDFEditorComponent from '../components/documents/PDFEditorComponent';
import DocumentEditorComponent from '../components/documents/DocumentEditorComponent';
import { aiAPI, documentAPI } from '../services/api';
import geminiApi from '../services/geminiApi';
import { 
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Create as CreateIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Save as SaveIcon,
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';

const DocumentUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State declarations
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Define the DocumentSection interface to match the one in DocumentEditorComponent
  interface DocumentSection {
    id: number;
    title: string;
    content: string;
  }

  const [documentData, setDocumentData] = useState<{
    title: string;
    sections: DocumentSection[];
    activeSection: number | null;
    metadata: {
      author: string;
      createdAt: string;
      updatedAt: string;
      generatedFromPrompt?: string;
      generatedBy?: string;
    }
  }>({
    title: '',
    sections: [],
    activeSection: 0,  // Set default active section
    metadata: {
      author: 'Current User',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });
  
  // Handle redirection to login while preserving the prompt
  const handleRedirectToLogin = useCallback(() => {
    // Save the current prompt to localStorage before redirecting
    if (aiPrompt.trim()) {
      localStorage.setItem('aiPrompt', aiPrompt);
    }
    navigate('/login');
  }, [aiPrompt, navigate]);
  
  // Check if user is authenticated - used throughout the component
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  // Check for tab parameter in URL and retrieve stored prompt
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    
    // Set the tab based on the URL parameter
    if (tabParam === 'ai') {
      setTabValue(1);
      
      // Retrieve stored prompt from localStorage if it exists
      const storedPrompt = localStorage.getItem('aiPrompt');
      if (storedPrompt) {
        setAiPrompt(storedPrompt);
        // Clear the stored prompt after retrieving it
        localStorage.removeItem('aiPrompt');
      }
    } else if (tabParam === 'upload' || !tabParam) {
      // If tab parameter is 'upload' or not specified, default to the upload tab (tab index 0)
      setTabValue(0);
    }
  }, [location]);
  
  const steps = ['Create/Upload Document', 'Edit & Enhance'];
  
  // Handle file upload
  const handleFileUpload = useCallback((file: File) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      handleRedirectToLogin();
      return;
    }
    
    console.log(`File upload handler called with file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
    setUploadedFile(file);
    
    // Extract file name without extension as document title
    const fileName = file.name.split('.').slice(0, -1).join('.');
    console.log(`Document title: ${fileName}`);
    
    // Get the extracted text from localStorage
    const extractedText = localStorage.getItem('extractedDocumentText') || '';
    console.log(`Extracted text from localStorage: ${extractedText.length} characters`);
    
    if (!extractedText && file.type !== 'application/pdf') {
      console.warn('No extracted text found in localStorage');
      setError('Failed to extract text from document. Please try again.');
      return;
    }
    
    // Parse the extracted text into sections
    const sections = extractedText ? parseContentIntoSections(extractedText) : [];
    console.log(`Parsed ${sections.length} sections from extracted text`);
    
    setDocumentData({
      ...documentData,
      title: fileName || 'Untitled Document',
      sections: sections.length > 0 ? sections : [
        { id: 1, title: 'Document Content', content: extractedText || 'No content extracted from document.' }
      ],
      activeSection: 0
    });
    console.log('Document data updated with extracted content');
    
    // Move to next step
    setActiveStep(1);
    console.log('Moving to step 1 (Edit & Enhance)');
    
    // Clean up localStorage
    localStorage.removeItem('extractedDocumentText');
    console.log('Cleaned up localStorage');
  }, [documentData, isAuthenticated, handleRedirectToLogin]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle AI prompt change
  const handleAiPromptChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAiPrompt(e.target.value);
  };
  
  // Handle AI document generation using Gemini API
  const handleGenerateDocument = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      handleRedirectToLogin();
      return;
    }
    
    if (!aiPrompt.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a prompt to generate a document',
        severity: 'error'
      });
      return;
    }
    
    setAiGenerating(true);
    
    try {
      console.log('Starting document generation with prompt:', aiPrompt);
      
      // Call Gemini API to generate legal document
      const generatedContent = await geminiApi.generateLegalDocument(aiPrompt);
      
      console.log('Received response from Gemini API:', generatedContent.substring(0, 100) + '...');
      
      if (!generatedContent || generatedContent.trim() === '') {
        throw new Error('Empty response received from Gemini API');
      }
      
      // Parse the generated content into sections
      // This is a simple parsing logic - in a real app, you might want more sophisticated parsing
      const sections = parseContentIntoSections(generatedContent);
      
      console.log('Parsed sections:', sections.length);
      
      // Extract a title from the prompt or the first section
      const title = extractTitle(aiPrompt, sections);
      
      // Update document data with Gemini-generated content
      setDocumentData({
        title: title,
        sections: sections,
        activeSection: 0,  // Set the first section as active by default
        metadata: {
          author: 'Current User',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          generatedFromPrompt: aiPrompt,
          generatedBy: 'AI Assistant'
        }
      });
      
      // Move to next step
      setActiveStep(1);
      
      setSnackbar({
        open: true,
        message: 'Document generated successfully with AI Assistant!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error generating document with Gemini API:', error);
      
      // Provide more detailed error message
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to generate document';
      console.error('Detailed error:', errorMessage);
      
      setSnackbar({
        open: true,
        message: `Failed to generate document: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setAiGenerating(false);
    }
  };
  
  // Helper function to parse generated content into sections
  const parseContentIntoSections = (content: string): {id: number, title: string, content: string}[] => {
    // Simple section detection based on common headers in legal documents
    const sectionRegex = /\n\s*(SECTION|Article|ARTICLE|Section|\d+\.|[IVX]+\.|[A-Z][A-Z\s]+:)\s*([^\n]+)/g;
    
    // Use a manual approach instead of matchAll to avoid TypeScript compatibility issues
    const matches: Array<{index: number, title: string, fullMatch: string}> = [];
    let match;
    
    // Reset regex before using it
    sectionRegex.lastIndex = 0;
    
    // Find all matches manually
    while ((match = sectionRegex.exec(content)) !== null) {
      matches.push({
        index: match.index,
        title: match[2].trim(),
        fullMatch: match[0]
      });
    }
    
    if (matches.length === 0) {
      // If no sections detected, create a simple structure
      return [
        { id: 1, title: 'Document', content: content }
      ];
    }
    
    const sections = [];
    let lastIndex = 0;
    
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const startIndex = match.index;
      const sectionTitle = match.title;
      
      // For the first section, include any content before it as an introduction
      if (i === 0 && startIndex > 0) {
        sections.push({
          id: sections.length + 1,
          title: 'Introduction',
          content: content.substring(0, startIndex).trim()
        });
      }
      
      // Calculate where this section ends (start of next section or end of content)
      const endIndex = i < matches.length - 1 ? 
        matches[i+1].index : 
        content.length;
      
      // Extract section content, skipping the header itself
      const headerLength = match.fullMatch.length;
      const sectionContent = content.substring(startIndex + headerLength, endIndex).trim();
      
      sections.push({
        id: sections.length + 1,
        title: sectionTitle,
        content: sectionContent
      });
      
      lastIndex = endIndex;
    }
    
    // If there's content after the last section, add it as a conclusion
    if (lastIndex < content.length - 1) {
      sections.push({
        id: sections.length + 1,
        title: 'Conclusion',
        content: content.substring(lastIndex).trim()
      });
    }
    
    return sections;
  };
  
  // Helper function to extract a title from the prompt or first section
  const extractTitle = (prompt: string, sections: {id: number, title: string, content: string}[]): string => {
    // Define common document types and subjects
    const documentTypes = [
      'contract', 'agreement', 'lease', 'nda', 'proposal', 'memorandum', 'letter', 
      'policy', 'terms', 'conditions', 'statement', 'report', 'plan', 'certificate', 
      'declaration', 'invoice', 'receipt', 'quote', 'estimate'
    ];
    
    // Define common contract subjects
    const contractSubjects = [
      'employment', 'service', 'consulting', 'freelance', 'software', 'development',
      'rental', 'lease', 'sale', 'purchase', 'licensing', 'subscription', 'partnership',
      'joint venture', 'confidentiality', 'non-disclosure', 'non-compete', 'maintenance',
      'support', 'supply', 'distribution', 'manufacturing', 'construction', 'marketing',
      'advertising', 'investment', 'loan', 'mortgage', 'property', 'real estate'
    ];
    
    // Try to extract parties involved (company names, individuals)
    const partiesRegex = /(?:between|with|for)\s+([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*)\s+(?:and|&)\s+([A-Z][A-Za-z]*(?:\s+[A-Z][A-Za-z]*)*)/i;
    const partiesMatch = prompt.match(partiesRegex);
    
    // Try to extract the primary subject and document type
    let documentType = '';
    let contractSubject = '';
    
    // Find document type
    for (const type of documentTypes) {
      if (prompt.toLowerCase().includes(type)) {
        documentType = type.charAt(0).toUpperCase() + type.slice(1);
        break;
      }
    }
    
    // Find contract subject
    for (const subject of contractSubjects) {
      if (prompt.toLowerCase().includes(subject)) {
        contractSubject = subject.charAt(0).toUpperCase() + subject.slice(1);
        break;
      }
    }
    
    // Extract specific purpose or description
    const purposeRegex = /(?:for|regarding|about|related to)\s+([\w\s]+?)(?:between|with|\.|\,|$)/i;
    const purposeMatch = prompt.match(purposeRegex);
    
    // Build the title based on available information
    if (partiesMatch && documentType) {
      // Format: "[Subject] [Type] between [Party1] and [Party2]"
      const party1 = partiesMatch[1].trim();
      const party2 = partiesMatch[2].trim();
      
      if (contractSubject) {
        return `${contractSubject} ${documentType} between ${party1} and ${party2}`;
      }
      return `${documentType} between ${party1} and ${party2}`;
    } 
    else if (purposeMatch && documentType) {
      // Format: "[Purpose] [Type]"
      const purpose = purposeMatch[1].trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      return `${purpose} ${documentType}`;
    }
    else if (contractSubject && documentType) {
      // Format: "[Subject] [Type]"
      return `${contractSubject} ${documentType}`;
    }
    
    // If no specific info found, try to extract key terms from the prompt
    // Skip common words and focus on domain-specific terms
    const skipWords = ['create', 'draft', 'generate', 'write', 'please', 'would', 'could', 
      'should', 'need', 'want', 'make', 'help', 'assist', 'prepare', 'document', 'contract', 
      'agreement', 'legal', 'formal', 'official', 'professional', 'business'];
    
    // Extract meaningful keywords from the prompt
    const keywords = prompt.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => 
        word.length > 3 && 
        !skipWords.includes(word) && 
        isNaN(parseInt(word))
      );
    
    if (keywords.length > 0) {
      // Take up to 3 significant keywords
      const significantWords = keywords.slice(0, 3)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
      
      // If we have a document type, use it; otherwise default to "Agreement"
      const docType = documentType || 'Agreement';
      return `${significantWords.join(' ')} ${docType}`;
    }
    
    // Check if the first section might contain a title
    if (sections.length > 0) {
      // Look for title in the first section content
      const firstSectionContent = sections[0].content.trim();
      const firstLine = firstSectionContent.split('\n')[0].trim();
      
      // If the first line looks like a title (not too long, not too short)
      if (firstLine.length > 5 && firstLine.length < 60 && 
          (firstLine.toUpperCase() === firstLine || 
           firstLine.charAt(0).toUpperCase() === firstLine.charAt(0))) {
        return firstLine;
      }
      
      // Check if any line in the first section starts with "AGREEMENT" or similar
      const lines = firstSectionContent.split('\n').slice(0, 5); // Check first 5 lines
      for (const line of lines) {
        if (line.toUpperCase().includes('AGREEMENT') || 
            line.toUpperCase().includes('CONTRACT') ||
            line.toUpperCase().includes('BETWEEN')) {
          return line.trim();
        }
      }
    }
    
    // Last resort - if we have a document type, use it with a generic descriptor
    if (documentType) {
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short'
      });
      return `${documentType} (${currentDate})`;
    }
    
    // Ultimate fallback
    return 'Business Agreement';
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Handle document analysis using Gemini API
  const handleAnalyzeDocument = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      handleRedirectToLogin();
      return;
    }
    
    if (!documentData || !documentData.sections || documentData.sections.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please upload or generate a document first',
        severity: 'error'
      });
      return;
    }
    
    setIsAnalyzing(true);
    
    try {
      // Prepare document text for analysis by concatenating all sections
      let documentText = '';
      const newline = String.fromCharCode(10); // ASCII for newline
      
      documentData.sections.forEach((section: any, index: number) => {
        documentText += section.title;
        documentText += newline;
        documentText += section.content;
        
        // Add separator between sections except for the last one
        if (index < documentData.sections.length - 1) {
          documentText += newline + newline;
        }
      });
      
      // Call Gemini API to analyze the document
      const analysis = await geminiApi.analyzeLegalDocument(documentText);
      
      // Set the analysis result
      setAnalysisResult(analysis);
      
      setSnackbar({
        open: true,
        message: 'Document analyzed successfully with AI Assistant!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error analyzing document with Gemini API:', error);
      setSnackbar({
        open: true,
        message: 'Failed to analyze document. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Handle section content change
  const handleSectionContentChange = (sectionId: number, newContent: string) => {
    if (!documentData || !documentData.sections) return;
    
    const updatedSections = documentData.sections.map((section: any) => {
      if (section.id === sectionId) {
        return { ...section, content: newContent };
      }
      return section;
    });
    
    setDocumentData({
      ...documentData,
      sections: updatedSections
    });
  };
  
  // Handle section deletion
  const handleDeleteSection = (sectionId: number) => {
    if (!documentData || !documentData.sections) return;
    
    const updatedSections = documentData.sections.filter((section: any) => section.id !== sectionId);
    
    setDocumentData({
      ...documentData,
      sections: updatedSections
    });
    
    // If the active section was deleted, reset activeSection
    if (activeSection === sectionId) {
      setActiveSection(null);
    }
  };
  
  // Handle section title editing
  const handleEditSectionTitle = (sectionId: number) => {
    if (!documentData || !documentData.sections) return;
    
    const section = documentData.sections.find((s: any) => s.id === sectionId);
    if (!section) return;
    
    const newTitle = prompt('Enter new section title:', section.title);
    if (newTitle === null || newTitle.trim() === '') return; // User cancelled or entered empty title
    
    const updatedSections = documentData.sections.map((section: any) => {
      if (section.id === sectionId) {
        return { ...section, title: newTitle.trim() };
      }
      return section;
    });
    
    setDocumentData({
      ...documentData,
      sections: updatedSections
    });
  };
  
  // Handle adding a new section
  const handleAddSection = () => {
    if (!documentData) {
      setDocumentData({
        title: 'New Document',
        sections: [],
        activeSection: 0,
        metadata: {
          author: 'Current User',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      });
    }
    
    const newSectionId = documentData && documentData.sections && documentData.sections.length > 0 
      ? Math.max(...documentData.sections.map((s: any) => s.id)) + 1 
      : 1;
    
    const newSection = {
      id: newSectionId,
      title: `Section ${newSectionId}`,
      content: ''
    };
    
    const updatedSections = documentData && documentData.sections 
      ? [...documentData.sections, newSection] 
      : [newSection];
    
    setDocumentData({
      ...documentData,
      sections: updatedSections
    });
    
    // Set the new section as active
    setActiveSection(newSectionId);
  };
  
  // Handle document simplification using Gemini API
  const handleSimplifyDocument = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      handleRedirectToLogin();
      return;
    }
    
    if (!documentData || !documentData.sections || documentData.sections.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please upload or generate a document first',
        severity: 'error'
      });
      return;
    }
    
    if (activeSection === null) {
      setSnackbar({
        open: true,
        message: 'Please select a section to simplify',
        severity: 'error'
      });
      return;
    }
    
    setIsAnalyzing(true);
    
    try {
      // Get the current section content
      const currentSection = documentData.sections.find((s: any) => s.id === activeSection);
      
      if (!currentSection) {
        throw new Error('No active section found');
      }
      
      // Call Gemini API to simplify the legal language
      const simplifiedText = await geminiApi.simplifyLegalLanguage(currentSection.content);
      
      // Update the section with simplified text
      const updatedSections = documentData.sections.map((section: any) => {
        if (section.id === activeSection) {
          return {
            ...section,
            content: simplifiedText
          };
        }
        return section;
      });
      
      // Update document data
      setDocumentData({
        ...documentData,
        sections: updatedSections
      });
      
      setSnackbar({
        open: true,
        message: 'Legal language simplified successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error simplifying document with Gemini API:', error);
      setSnackbar({
        open: true,
        message: 'Failed to simplify document. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Handle document save
  const handleSaveDocument = async (document: any) => {
    setLoading(true);
    setError(null);
    
    try {
      // In a real implementation, you would upload the file and save the document data
      // For demo purposes, we'll simulate a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Call the API to save the document
      const response = await documentAPI.create({
        ...document,
        fileData: uploadedFile ? {
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
        } : undefined
      });
      
      // Navigate to the document view page
      navigate(`/documents/${response.data.id}`);
    } catch (error) {
      console.error('Error saving document:', error);
      setError('Failed to save document. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle AI request
  const handleAIRequest = async (prompt: string, context: string) => {
    try {
      console.log('AI Assistant request with prompt:', prompt);
      console.log('Context length:', context.length);
      
      // If context is empty but we have document data, use the first section as context
      if (!context.trim() && documentData && documentData.sections && documentData.sections.length > 0) {
        console.log('Context is empty, using first section of document as context');
        context = documentData.sections[0].content;
        console.log('New context length:', context.length);
      }
      
      // If context is still empty, generate a placeholder message
      if (!context.trim()) {
        console.log('No document content available for context');
        return 'Please select a document section with content for the AI to improve, or generate a document first.';
      }
      
      // Use the dedicated improveLegalText function from Gemini API
      const result = await geminiApi.improveLegalText(context, prompt);
      console.log('AI Assistant response received, length:', result.length);
      
      return result;
    } catch (error) {
      console.error('Error getting AI response:', error);
      throw new Error('Failed to get AI response');
    }
  };
  
  // Handle step back
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Render step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
            <Box sx={{ 
              background: "linear-gradient(135deg, #1a365d 0%, #2d8cff 100%)",
              borderRadius: "16px",
              py: 6,
              px: { xs: 3, md: 6 },
              mb: 5,
              color: "white",
              textAlign: "center"
            }}>
              <Typography 
                variant="h3" 
                component="h1" 
                fontWeight="800" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: "1.75rem", md: "2.5rem" },
                  mb: 2 
                }}
              >
                Create Your Legal Document
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  maxWidth: "800px", 
                  mx: "auto",
                  mb: 4,
                  opacity: 0.9,
                  fontSize: { xs: "1rem", md: "1.1rem" }
                }}
              >
                Upload an existing document or generate a new one with AI in seconds
              </Typography>
              
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="document creation options" 
                sx={{ 
                  mb: 0,
                  "& .MuiTabs-indicator": {
                    backgroundColor: "white",
                    height: 3
                  },
                  "& .MuiTab-root": {
                    color: "rgba(255,255,255,0.7)",
                    fontSize: "1rem",
                    fontWeight: 600,
                    "&.Mui-selected": {
                      color: "white"
                    }
                  }
                }}
                centered
              >
                <Tab icon={<CloudUploadIcon />} label="Upload Document" iconPosition="start" />
                <Tab icon={<AutoAwesomeIcon />} label="AI Generate" iconPosition="start" />
              </Tabs>
            </Box>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, md: 5 }, 
                mb: 5, 
                borderRadius: "16px",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 10px 40px rgba(0,0,0,0.06)"
              }}
            >
              {tabValue === 0 ? (
                <Box>
                  <Typography 
                    variant="h5" 
                    fontWeight="700" 
                    gutterBottom
                    sx={{ color: "#1a365d", mb: 2 }}
                  >
                    Upload Your Contract Document
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                    Upload your contract document to edit, analyze, and enhance it with AI. We support PDF, Word, and text files.
                  </Typography>
                  
                  <FileUploadComponent 
                    onFileUpload={handleFileUpload}
                    allowedFileTypes={[".pdf", ".doc", ".docx", ".txt"]}
                    maxFileSizeMB={10}
                  />
                  {documentData && documentData.sections && documentData.sections.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Document Editor
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      
                      <Grid container spacing={3}>
                        {/* Section List - Left Side */}
                        <Grid item xs={12} md={4}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                              Sections
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {documentData.sections.map((section: any) => (
                                <Button
                                  key={section.id}
                                  variant={activeSection === section.id ? "contained" : "outlined"}
                                  color={activeSection === section.id ? "secondary" : "primary"}
                                  sx={{ 
                                    justifyContent: 'flex-start', 
                                    textAlign: 'left',
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: activeSection === section.id ? 600 : 400
                                  }}
                                  onClick={() => setActiveSection(section.id)}
                                >
                                  {section.title}
                                </Button>
                              ))}
                              
                              <Button
                                variant="text"
                                startIcon={<AddIcon />}
                                onClick={handleAddSection}
                                sx={{ mt: 2, justifyContent: 'flex-start' }}
                              >
                                Add New Section
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                        
                        {/* Section Editor - Right Side */}
                        <Grid item xs={12} md={8}>
                          {activeSection !== null && (
                            <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                              {documentData.sections.map((section: any) => (
                                section.id === activeSection && (
                                  <Box key={section.id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                        {section.title}
                                      </Typography>
                                      <Box>
                                        <Button
                                          size="small"
                                          startIcon={<EditIcon />}
                                          onClick={() => handleEditSectionTitle(section.id)}
                                          sx={{ mr: 1 }}
                                        >
                                          Edit Title
                                        </Button>
                                        <Button
                                          size="small"
                                          startIcon={<DeleteIcon />}
                                          onClick={() => handleDeleteSection(section.id)}
                                          color="error"
                                          variant="outlined"
                                        >
                                          Delete
                                        </Button>
                                      </Box>
                                    </Box>
                                    
                                    <TextField
                                      multiline
                                      fullWidth
                                      minRows={10}
                                      value={section.content}
                                      onChange={(e) => handleSectionContentChange(section.id, e.target.value)}
                                      variant="outlined"
                                      sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          fontSize: '1rem',
                                          lineHeight: 1.6
                                        }
                                      }}
                                    />
                                  </Box>
                                )
                              ))}
                            </Paper>
                          )}
                          
                          {activeSection === null && documentData.sections.length > 0 && (
                            <Paper elevation={0} sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h6" color="text.secondary">
                                Select a section to edit
                              </Typography>
                            </Paper>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  
                  {analysisResult && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        AI Document Analysis
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AnalyticsIcon color="secondary" sx={{ mr: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Powered by AI
                          </Typography>
                        </Box>
                        <Typography variant="body1" component="div" sx={{ 
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          '& h1, & h2, & h3, & h4': {
                            color: 'primary.main',
                            fontWeight: 500,
                            mt: 2,
                            mb: 1
                          },
                          '& ul, & ol': {
                            pl: 3,
                            mb: 2
                          }
                        }}>
                          {analysisResult}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 4, mb: 2 }}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'primary.main' }}>
                        AI Document Tools
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                              Document Analysis
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Get a comprehensive analysis of your document including key points, potential risks, and improvement suggestions.
                            </Typography>
                            <Button
                              variant="contained"
                              color="info"
                              onClick={handleAnalyzeDocument}
                              disabled={isAnalyzing || !documentData || loading}
                              startIcon={<AnalyticsIcon />}
                              sx={{ mt: 'auto' }}
                            >
                              {isAnalyzing ? 'Analyzing...' : 'Analyze Document'}
                            </Button>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                              Simplify Legal Language
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Convert complex legal jargon into plain, easy-to-understand language while preserving the legal meaning.
                            </Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handleSimplifyDocument}
                              disabled={isAnalyzing || !documentData || loading || activeSection === null}
                              startIcon={<TranslateIcon />}
                              sx={{ mt: 'auto' }}
                            >
                              {isAnalyzing ? 'Simplifying...' : 'Simplify Selected Section'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleSaveDocument(documentData)}
                          disabled={loading || !documentData}
                          startIcon={<SaveIcon />}
                          size="large"
                        >
                          Save Document
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                  
                  <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={true}
                      sx={{ 
                        px: 4, 
                        py: 1.5, 
                        borderRadius: "8px",
                        fontWeight: 600,
                        boxShadow: "0 4px 14px rgba(45,140,255,0.4)",
                        opacity: 0.5
                      }}
                    >
                      Continue
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography 
                    variant="h5" 
                    fontWeight="700" 
                    gutterBottom
                    sx={{ color: "#1a365d", mb: 2 }}
                  >
                    Generate Document with AI
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                    Describe the contract you need, and our AI will generate it for you. Be as specific as possible for best results.
                  </Typography>
                  
                  <Box sx={{ 
                    border: "1px solid rgba(0,0,0,0.1)", 
                    borderRadius: "12px", 
                    p: 3, 
                    mb: 4,
                    bgcolor: "rgba(45,140,255,0.03)"
                  }}>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: "#1a365d" }}>
                      Example prompts:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      • Create a consulting agreement between ABC Consulting and XYZ Corp for web development services, with a 6-month term and $10,000 monthly fee.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      • Draft an NDA for protecting confidential information between two technology companies working on a joint AI project.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Generate a rental agreement for a commercial property in New York, with a 2-year lease term and quarterly payment schedule.
                    </Typography>
                  </Box>
                  
                  <TextField
                    label="Describe the contract you need"
                    placeholder="Enter your requirements here..."
                    multiline
                    rows={5}
                    fullWidth
                    value={aiPrompt}
                    onChange={handleAiPromptChange}
                    variant="outlined"
                    sx={{ 
                      mb: 4,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                  
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<PsychologyIcon />}
                      onClick={handleGenerateDocument}
                      disabled={aiGenerating || !aiPrompt.trim()}
                      sx={{ 
                        px: 4, 
                        py: 1.5, 
                        borderRadius: "8px",
                        fontWeight: 600,
                        boxShadow: "0 4px 14px rgba(45,140,255,0.4)"
                      }}
                    >
                      {aiGenerating ? "Generating..." : "Generate Document"}
                    </Button>
                  </Box>
                  
                  {aiGenerating && (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", mt: 4 }}>
                      <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Creating your document... This may take a minute.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 3, md: 5 }, 
                borderRadius: "16px",
                bgcolor: "#f8faff",
                border: "1px solid rgba(0,0,0,0.05)"
              }}
            >
              <Typography 
                variant="h5" 
                fontWeight="700" 
                gutterBottom 
                sx={{ 
                  color: "#1a365d", 
                  mb: 4, 
                  textAlign: "center" 
                }}
              >
                How It Works
              </Typography>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    textAlign: "center",
                    p: 3,
                    height: "100%",
                    border: "1px solid rgba(0,0,0,0.05)",
                    borderRadius: "12px",
                    bgcolor: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)"
                    }
                  }}>
                    <Box sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: "50%", 
                      bgcolor: "rgba(45,140,255,0.1)", 
                      display: "flex", 
                      justifyContent: "center", 
                      alignItems: "center",
                      mx: "auto",
                      mb: 3
                    }}>
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#2d8cff" }} />
                    </Box>
                    <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: "#1a365d" }}>
                      1. Create or Upload
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload your document or use AI to generate a new contract from your description.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    textAlign: "center",
                    p: 3,
                    height: "100%",
                    border: "1px solid rgba(0,0,0,0.05)",
                    borderRadius: "12px",
                    bgcolor: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)"
                    }
                  }}>
                    <Box sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: "50%", 
                      bgcolor: "rgba(45,140,255,0.1)", 
                      display: "flex", 
                      justifyContent: "center", 
                      alignItems: "center",
                      mx: "auto",
                      mb: 3
                    }}>
                      <CreateIcon sx={{ fontSize: 40, color: "#2d8cff" }} />
                    </Box>
                    <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: "#1a365d" }}>
                      2. Edit & Enhance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Edit your document and use AI to improve clauses, analyze risks, and suggest alternatives.
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    textAlign: "center",
                    p: 3,
                    height: "100%",
                    border: "1px solid rgba(0,0,0,0.05)",
                    borderRadius: "12px",
                    bgcolor: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)"
                    }
                  }}>
                    <Box sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: "50%", 
                      bgcolor: "rgba(45,140,255,0.1)", 
                      display: "flex", 
                      justifyContent: "center", 
                      alignItems: "center",
                      mx: "auto",
                      mb: 3
                    }}>
                      <DescriptionIcon sx={{ fontSize: 40, color: "#2d8cff" }} />
                    </Box>
                    <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: "#1a365d" }}>
                      3. Download & Share
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download your enhanced document or share it securely with stakeholders.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );
      case 1:
        // Determine which editor to use based on file type
        if (uploadedFile?.type === 'application/pdf') {
          return (
            <PDFEditorComponent 
              file={uploadedFile}
              onSave={handleSaveDocument}
              onAIRequest={handleAIRequest}
            />
          );
        } else {
          // For non-PDF files or text-based editing
          return (
            <DocumentEditorComponent 
              document={{
                title: documentData.title,
                sections: documentData.sections.length > 0 ? documentData.sections : [
                  { id: 1, title: 'Introduction', content: 'This Agreement is made and entered into as of [DATE], by and between the undersigned parties.' },
                  { id: 2, title: 'Scope of Services', content: 'The Contractor shall provide the following services: [DESCRIPTION OF SERVICES].' },
                  { id: 3, title: 'Compensation', content: 'The Client shall pay the Contractor a fee of [AMOUNT] for the services provided.' },
                  { id: 4, title: 'Term and Termination', content: 'This Agreement shall commence on [START DATE] and continue until [END DATE], unless terminated earlier.' },
                  { id: 5, title: 'Confidentiality', content: 'The Contractor agrees to maintain the confidentiality of all proprietary information provided by the Client.' },
                ],
                activeSection: documentData.activeSection
              }}
              onSave={handleSaveDocument}
              onAIRequest={handleAIRequest}
              onSectionChange={(index) => {
                setDocumentData(prev => ({
                  ...prev,
                  activeSection: index
                }));
              }}
              onSectionContentChange={(content) => {
                if (documentData.activeSection !== null) {
                  const updatedSections = [...documentData.sections];
                  updatedSections[documentData.activeSection].content = content;
                  setDocumentData(prev => ({
                    ...prev,
                    sections: updatedSections
                  }));
                }
              }}
              onSectionTitleChange={(index, title) => {
                const updatedSections = [...documentData.sections];
                updatedSections[index].title = title;
                setDocumentData(prev => ({
                  ...prev,
                  sections: updatedSections
                }));
              }}
              onAddSection={() => {
                const newSection = {
                  id: Date.now(),
                  title: `New Section ${documentData.sections.length + 1}`,
                  content: ''
                };
                setDocumentData(prev => ({
                  ...prev,
                  sections: [...prev.sections, newSection],
                  activeSection: prev.sections.length
                }));
              }}
              onRemoveSection={(index) => {
                if (documentData.sections.length <= 1) {
                  return;
                }
                
                const updatedSections = [...documentData.sections];
                updatedSections.splice(index, 1);
                
                let newActiveSection = documentData.activeSection;
                if (documentData.activeSection === index) {
                  newActiveSection = Math.max(0, index - 1);
                } else if (documentData.activeSection !== null && documentData.activeSection > index) {
                  newActiveSection = documentData.activeSection - 1;
                }
                
                setDocumentData(prev => ({
                  ...prev,
                  sections: updatedSections,
                  activeSection: newActiveSection
                }));
              }}
              onPrint={() => window.print()}
            />
          );
        }
      default:
        return 'Unknown step';
    }
  };
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: 10, pb: 8 }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 5,
            bgcolor: 'background.paper',
            boxShadow: '0 10px 40px rgba(58, 54, 224, 0.1)',
            background: 'linear-gradient(145deg, #ffffff, #f8f9ff)',
            border: '1px solid rgba(58, 54, 224, 0.05)',
            overflow: 'hidden',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="700" 
            gutterBottom
            sx={{ color: 'primary.main', mb: 3 }}
          >
            {documentData.title || 'New Document'}
          </Typography>
          
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: 4,
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'primary.main'
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'primary.main'
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 3,
                '& .MuiAlert-icon': {
                  color: 'error.main'
                }
              }}
            >
              {error}
            </Alert>
          )}
          
          {getStepContent(activeStep)}
          
          {activeStep !== 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
              <Button 
                variant="outlined"
                color="primary"
                onClick={handleBack} 
                sx={{ 
                  mr: 1,
                  fontWeight: 600,
                  borderRadius: 3,
                  px: 3,
                  py: 1.2
                }}
              >
                Back to Upload
              </Button>
            </Box>
          )}
        </Paper>
        
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(58, 54, 224, 0.15)',
              '& .MuiAlert-icon': {
                fontSize: '1.25rem'
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default DocumentUploadPage;
